import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../api/axiosInstance";

export const useGetChatSessions = () => {
  return useQuery({
    queryKey: ["get-chat-sessions"],
    queryFn: async () => {
      const { data } = await axios.get("/api/chatbot/sessions");
      return data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetChatMessages = (sessionId: number) => {
  return useQuery({
    queryKey: ["get-chat-messages", sessionId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/chatbot/messages/${sessionId}`);
      return data;
    },
    refetchOnWindowFocus: false,
    enabled: !!sessionId, // Only fetch when sessionId is truthy
  });
};

export const useCreateChatSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.post("/api/chatbot/create-session");
      return data;
    },
    onSuccess: () => {
    },
  });
};

export const useSendQuery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      query,
    }: {
      sessionId: number;
      query: string;
    }) => {
      const { data } = await axios.post(
        `/api/chatbot/send-query/${sessionId}`,
        { query }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the chat messages query to refetch after sending a new query
      queryClient.invalidateQueries({ queryKey: ["get-chat-sessions"] });
      queryClient.invalidateQueries({
        queryKey: ["get-chat-messages", variables.sessionId],
      });
    },
    onError: (error: any) => {
      // Enhanced error handling for specific error types
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error("Network connection failed. Please check your internet.");
      }
      if (error.response?.status === 429) {
        throw new Error("Too many requests. Please wait a moment.");
      }
      if (error.response?.status === 503) {
        throw new Error("Service temporarily unavailable. Please try again later.");
      }
      if (error.response?.status === 408) {
        throw new Error("Request timed out. Please try again.");
      }
      // Let other errors bubble up with their original messages
      throw new Error("Application Error. Please contact support.");
    },
  });
};

export const useRegenerateResponse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, messageId }: { sessionId: number; messageId: number }) => {
      const { data } = await axios.post(`/api/chatbot/regenerate-response/${sessionId}`, { messageId });
      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the chat messages query to refetch after regenerating response
      queryClient.invalidateQueries({ queryKey: ["get-chat-messages", variables.sessionId] });
    },
    onError: (error: any) => {
      // Enhanced error handling for specific error types
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error("Network connection failed. Please check your internet.");
      }
      if (error.response?.status === 429) {
        throw new Error("Too many requests. Please wait a moment.");
      }
      if (error.response?.status === 503) {
        throw new Error("Service temporarily unavailable. Please try again later.");
      }
      if (error.response?.status === 408) {
        throw new Error("Request timed out. Please try again.");
      }
      // Let other errors bubble up with their original messages
      throw new Error("Application Error. Please contact support.");
    },
  });
};

export const useRenameChatSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, newTitle }: { sessionId: number; newTitle: string }) => {
      const { data } = await axios.put(`/api/chatbot/rename-session/${sessionId}`, {
        newTitle,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-chat-sessions"] });
    },
  });
};

export const useDeleteChatSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: number) => {
      const { data } = await axios.delete(`/api/chatbot/delete-session/${sessionId}`);
      return data;
    },
    onSuccess: () => {
      // Invalidate the chat sessions query to refetch after deletion
      queryClient.invalidateQueries({ queryKey: ["get-chat-sessions"] });
    },
  });
}


export const useSearchChatContent = (searchTerm: string) => {
  return useQuery({
    queryKey: ['search-chat-content', searchTerm],
    queryFn: async () => {
      const { data } = await axios.get(`/api/chatbot/search-messages?searchTerm=${encodeURIComponent(searchTerm)}`);
      return data;
    },
    enabled: !!searchTerm && searchTerm.trim().length > 1,
    refetchOnWindowFocus: false,
  });
};